import { useTranslation } from 'react-i18next';
import type { SelectOption } from '@/shared/ui';
import { CHART_TYPES, COLOR_PRESETS } from '../options';

/** Colour dot for select options ("Random" = a conic palette dot). */
export function ColorDot({ color }: { color: string | null }) {
  return color ? (
    <span aria-hidden className="size-12 shrink-0 rounded-full" style={{ backgroundColor: color }} />
  ) : (
    <span aria-hidden className="size-12 shrink-0 rounded-full bg-[conic-gradient(#0275ff,#1ecd15,#f4b400,#df0259,#7059ff,#0275ff)]" />
  );
}

/** "Random" (= automatic colour, stored as null) in colour selects. */
export const AUTO_COLOR = 'auto';
/** Select value of a stored colour (presets in their canonical case, so `#0275ff` selects "Ko'k"). */
export const colorValue = (color: string | null | undefined) =>
  !color ? AUTO_COLOR : (COLOR_PRESETS.find((c) => c.value.toLowerCase() === color.toLowerCase())?.value ?? color);
export const colorFromValue = (value: string | null | undefined) => (!value || value === AUTO_COLOR ? null : value);

/**
 * "RANGI" options (586:16515): "Random" + the 8 accent swatches (556:16647). A stored colour that is not a preset
 * (set elsewhere, e.g. seeded `#35B5EC`) is listed as its HEX so the select never shows an empty value.
 */
export function useColorOptions(current?: string | null): SelectOption<string>[] {
  const { t } = useTranslation('devices');
  const presets = COLOR_PRESETS.map((c) => ({ value: c.value as string, label: t(`colors.${c.key}`), icon: <ColorDot color={c.value} /> }));
  const custom =
    current && current !== AUTO_COLOR && !COLOR_PRESETS.some((c) => c.value.toLowerCase() === current.toLowerCase())
      ? [{ value: current, label: current.toUpperCase(), icon: <ColorDot color={current} /> }]
      : [];
  return [{ value: AUTO_COLOR, label: t('colors.auto'), icon: <ColorDot color={null} /> }, ...presets, ...custom];
}

/** "DIAGRAMMA TURI" options: Random (auto) · Chiziqli · Ustunli · Maydonli · Nuqtali · Pog'onali. */
export function useChartTypeOptions(): SelectOption<string>[] {
  const { t } = useTranslation('devices');
  return CHART_TYPES.map((c) => ({ value: c, label: t(`chartTypes.${c}`) }));
}
